import { usePatientDevices } from 'infrastructure/hooks/device/use-patient-devices';
import { usePatientInfo } from 'infrastructure/hooks/patient/use-patient-info';
import {
  useRpmPatientActivity,
  useSocketActions,
} from 'infrastructure/hooks/rpm';
import useAlert from 'infrastructure/hooks/utils/use-alert';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from 'store';
import { BroadcastTopics } from 'services/wsService';
import useUser from 'utils/useUser';
import { ActivityStatus, ReadingStatus } from 'infrastructure/enums';
import { useSocketOn } from 'infrastructure/hooks/websockets/use-socket-on';

import { useNewReadings } from './use-new-readings';
import { useReadingReviewed } from './use-reading-reviewed';

import type {
  IAsignSocketData,
  ISocketInteractiveTimeData,
  IUpdateReadingAction,
} from 'infrastructure/interfaces/i-rpm-patient-activity';

interface Props {
  patientGuid: string;
  type?: ActivityStatus;
  pageSize?: number;
  startTime?: number;
  endTime?: number;
  onReadingsRefetch?: () => void;
}

const getStatus = (type?: ActivityStatus) => {
  if (type === ActivityStatus.UnactionedCritical)
    return [ReadingStatus.Critical];
  if (type === ActivityStatus.UnactionedNormalRisk)
    return [ReadingStatus.Normal, ReadingStatus.Risk];
  return undefined;
};

export const usePatientActivity = ({
  patientGuid,
  type,
  pageSize,
  startTime: propsStartTime,
  endTime: propsEndTime,
  onReadingsRefetch,
}: Props) => {
  const { t } = useTranslation();
  const { Alert, showAlert } = useAlert();
  const { isAgencyDoctor } = useUser();

  const defaultStatus = type ?? ActivityStatus.Actioned;

  const [sortByDate, setSortByDate] = useState(false);
  const [status, setStatus] = useState<ActivityStatus>(defaultStatus);
  const [page, setPage] = useState(1);
  const { isMedicalAssistantRole } = useUser();
  const { patientInfo, loading: patientInfoLoading } = usePatientInfo({
    patientGuid,
  });
  const { loading: isLoadingPatientDevices, devices } = usePatientDevices({
    patientGuid,
  });
  const [startTime, endTime] = useAppSelector<[number, number]>(
    (state) => state.patient.dates,
  );
  const deviceIds = devices?.map((device) => device.deviceId).join(',') ?? '';

  const statusForCheck = type ?? status;

  const activityStatus = getStatus(type);

  const {
    activity,
    isLoading: isLoadingActivity,
    isFetching: isFetchingActivity,
    remove,
    queryKey,
    refetch,
    isRefetching,
  } = useRpmPatientActivity({
    clinicGuid: patientInfo?.clinicGuid,
    deviceIds,
    patientGuid,
    reviewed: statusForCheck === ActivityStatus.Actioned,
    onlyIsErroneous: status === ActivityStatus.Erroneous,
    reverse: sortByDate,
    allowCriticals: isMedicalAssistantRole ? true : undefined,
    startTime: propsStartTime ?? startTime,
    endTime: propsEndTime ?? endTime,
    meta: { page, items: pageSize },
    status: activityStatus,
  });

  const actions = useAppSelector((state) => state.activityActions.actions);
  const {
    activityActions: { resetActions },
  } = useAppDispatch();

  const { onActionChange, reservedReadingAction, onInteractiveTimeChange } =
    useSocketActions(queryKey);

  const isLoading =
    !isRefetching &&
    (patientInfoLoading ||
      isLoadingPatientDevices ||
      isLoadingActivity ||
      isFetchingActivity);

  const hasUnsavedMessage = Object.values(actions).some((action) =>
    Boolean(action?.message?.length),
  );

  useNewReadings({
    type,
    patientGuid,
    hasUnsavedMessage,
    setPage,
    refetch,
    page,
    resetActions,
    onReadingsRefetch,
  });

  useReadingReviewed({
    patientGuid,
    hasUnsavedMessage,
    setPage,
    refetch,
    page,
    onReadingsRefetch,
  });

  const onSort = (field: string) => {
    if (field === 'createdAt') setSortByDate((prev) => !prev);
    if (field === '') setSortByDate(false);
  };

  const handleChangeType = async (v: ActivityStatus) => {
    if (hasUnsavedMessage) {
      const answer = await showAlert({
        title: t('confirm.title'),
        messageTitle: t('messages.confirmUnsavedChangesTitle'),
        message: t('messages.confirmUnsavedChangesMessage'),
        successBtnLabel: t('controls.yes'),
        rejectBtnLabel: t('controls.no'),
      });
      if (!answer) return;
      resetActions();
    }

    setPage(1);
    setStatus(v);
    remove();
  };

  useEffect(() => {
    return () => {
      resetActions();
    };
  }, []);

  useSocketOn<IUpdateReadingAction>(
    BroadcastTopics.NewReadingAction,
    (data) => {
      onActionChange({ ...data, newAction: true });
    },
  );

  useSocketOn<IAsignSocketData>(
    BroadcastTopics.ReservedReadingStateChange,
    (data) => {
      if (isAgencyDoctor && type === ActivityStatus.Unactioned) {
        reservedReadingAction(data);
      }
    },
  );

  useSocketOn<ISocketInteractiveTimeData>(
    BroadcastTopics.InteractiveTimeInsert,
    onInteractiveTimeChange,
  );

  useEffect(() => {
    setPage(1);
  }, [startTime, endTime, propsStartTime, propsEndTime]);

  return {
    activity,
    queryKey,
    Alert,
    isLoading,
    isFetching: isFetchingActivity,
    status,
    page,
    setPage,
    handleChangeType,
    onSort,
  };
};
